/**
 * Reviews carousel.
 *
 * The cards remain a native scroll-snap row. This file adds the optional
 * autoplay that used to live inline in sections/reviews.liquid, builds an
 * accessible dot control for every rendered card, and — on a product page with
 * Judge.me reviews — appends those reviews behind the hand-picked quotes.
 */
(() => {
  'use strict';

  /* A section can emit this asset more than once on the same page. The first
     execution owns the document listeners; later executions only rescan for a
     section that may have just been inserted by the theme editor. */
  if (window.__liturgyReviews) {
    window.__liturgyReviews.init(document);
    return;
  }

  const ROOT_SELECTOR = '.rev';
  const CARD_SELECTOR = '.rev__card';
  const DOT_HOST_SELECTOR = '[data-reviews-dots], .rev__dots';
  const NAV_HOST_SELECTOR = '[data-reviews-nav], .rev__nav';
  const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, [contenteditable="true"]';
  const DRAG_THRESHOLD = 5;
  const DRAG_COMMIT_RATIO = 0.14;
  const DRAG_COMMIT_MAX = 64;
  /* Chevrons drawn at the same 1.5 weight the theme's other slider arrows use,
     on a 16 box so the glyph optically matches the 20px card stars. */
  const CHEVRON = {
    prev: '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    next: '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };
  const MAX_VISIBLE_DOTS = 12;
  /* Judge.me feed. Ten per request is the brief; 30 characters is roughly the
     point below which a review reads as a rating with a word attached ("Skin
     Fluff") rather than something a shopper would stop to read. */
  const FEED_PER_PAGE = 10;
  const FEED_MIN_TEXT = 30;
  const FEED_TIMEOUT = 8000;
  /* Where the row stops. Nobody is still swiping past twenty, and the rating
     line above links to Judge.me's full list for anyone who wants the rest. */
  const FEED_MAX = 20;
  const instances = new Set();
  const feeds = new Set();
  let generatedId = 0;

  const numberOrZero = (value) => {
    if (typeof value === 'boolean') return 0;
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : 0;
  };

  /* Accept milliseconds (the section's autoplay_ms value) and the raw schema
     value in seconds. The latter is always 2-12, so values below 100 are
     unambiguous here. */
  const toMilliseconds = (value) => {
    const number = numberOrZero(value);
    return number && number < 100 ? number * 1000 : number;
  };

  const isFalse = (value) =>
    value === false || ['false', '0', 'off'].includes(String(value).toLowerCase());

  class ReviewsCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.rev__track');
      this.cards = this.track ? [...this.track.querySelectorAll(CARD_SELECTOR)] : [];
      this.listeners = [];
      this.timer = null;
      this.focusTimer = null;
      this.clickTimer = null;
      this.scrollFrame = null;
      this.visibilityObserver = null;
      this.cardObserver = null;
      this.resizeObserver = null;
      this.hovered = false;
      this.held = false;
      this.dragPointerId = null;
      this.dragStartX = 0;
      this.dragCurrentX = 0;
      this.dragStartScrollLeft = 0;
      this.dragStartIndex = 0;
      this.dragging = false;
      this.suppressClick = false;
      this.focused = false;
      this.activeIndex = 0;
      this.onScreen = !('IntersectionObserver' in window);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayInterval = this._readAutoplayInterval();
      this.generatedDotHost = false;
      this.generatedTrackId = false;
      this.generatedCardIds = [];

      if (!this.track || !this.cards.length) return;

      this._ensureIds();
      this._buildDots();
      this._buildNav();
      this.focused = this._containsFocus();
      this._bind();
      this._observe();
      this._requestPositionSync();
      this._syncAutoplay();

      root.dataset.reviewsInitialized = 'true';
      root.__reviewsCarousel = this;
    }

    _readAutoplayInterval() {
      let config = {};
      const configElement = this.root.querySelector('[data-reviews-config]');

      if (configElement) {
        try {
          config = JSON.parse(configElement.textContent) || {};
        } catch (error) {
          config = {};
        }
      }

      const enabled = [
        this.track && this.track.dataset.autoplayEnabled,
        this.root.dataset.autoplayEnabled,
        config.autoplayEnabled,
        config.enabled
      ].find((value) => value !== undefined);

      if (isFalse(enabled)) return 0;

      const sources = [this.track && this.track.dataset, this.root.dataset];
      const keys = ['reviewsAutoplayMs', 'autoplayMs', 'autoplayInterval', 'autoplay'];

      for (const source of sources) {
        if (!source) continue;
        for (const key of keys) {
          if (source[key] !== undefined && source[key] !== '') {
            return toMilliseconds(source[key]);
          }
        }
      }

      return toMilliseconds(
        config.autoplayMs ?? config.autoplayInterval ?? config.interval ?? config.autoplay
      );
    }

    _ensureIds() {
      if (!this.track.id) {
        const section = this.root.closest('.shopify-section');
        const sectionId = section && section.id ? section.id.replace(/^shopify-section-/, '') : ++generatedId;
        this.track.id = `reviews-track-${sectionId}`;
        this.generatedTrackId = true;
      }

      this.cards.forEach((card, index) => {
        if (card.id) return;
        card.id = `${this.track.id}-card-${index + 1}`;
        this.generatedCardIds.push(card);
      });
    }

    _buildDots() {
      this.dotHost = this.root.querySelector(DOT_HOST_SELECTOR);

      if (!this.dotHost) {
        this.dotHost = document.createElement('div');
        this.dotHost.className = 'rev__dots';
        this.dotHost.dataset.reviewsDots = '';
        (this.track.parentElement || this.root).appendChild(this.dotHost);
        this.generatedDotHost = true;
      }

      this.dotHost.setAttribute('role', 'navigation');
      if (!this.dotHost.hasAttribute('aria-label')) {
        this.dotHost.setAttribute('aria-label', 'Review navigation');
      }
      this.dotHost.style.setProperty('--rev-dots-visible', Math.min(this.cards.length, MAX_VISIBLE_DOTS));

      this.dotTrack = document.createElement('div');
      this.dotTrack.className = 'rev__dots-track';
      this.dotTrack.dataset.reviewsDotsTrack = '';

      const fragment = document.createDocumentFragment();
      this.dotButtons = this.cards.map((card, index) => {
        const button = document.createElement('button');
        const author = card.querySelector('.rev__author');
        const authorText = author ? author.textContent.trim().replace(/\s+/g, ' ') : '';
        const suffix = authorText ? `, ${authorText.slice(0, 80)}` : '';

        button.type = 'button';
        button.className = 'rev__dot';
        button.dataset.reviewsDot = String(index);
        button.setAttribute('aria-controls', card.id);
        button.setAttribute('aria-label', `Show review ${index + 1} of ${this.cards.length}${suffix}`);
        fragment.appendChild(button);
        return button;
      });

      this.dotTrack.appendChild(fragment);
      this.dotHost.replaceChildren(this.dotTrack);
      this._setActive(0, true);
    }

    /* The desktop control. Built here rather than server-rendered for the same
       reason the dots are: an arrow that cannot move anything is worse than no
       arrow, and this file is the only thing that can move the row.

       Above 768px this is the ONLY affordance. The dot rail is display:none
       there, the scrollbar is hidden by the section's CSS, and a mouse wheel is
       vertical — over a horizontal scroller the browser hands that scroll to
       the page, which is correct and which left the row unreachable. */
    _buildNav() {
      this.navHost = this.root.querySelector(NAV_HOST_SELECTOR);
      if (!this.navHost) return;

      this.navHost.setAttribute('role', 'group');
      if (!this.navHost.hasAttribute('aria-label')) {
        this.navHost.setAttribute('aria-label', 'Scroll reviews');
      }

      const make = (direction, label) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rev__nav-btn';
        button.dataset.reviewsNavDirection = direction;
        button.setAttribute('aria-label', label);
        button.setAttribute('aria-controls', this.track.id);
        button.innerHTML = CHEVRON[direction];
        return button;
      };

      this.navButtons = [
        make('prev', 'Previous review'),
        make('next', 'Next review')
      ];
      this.navHost.replaceChildren(...this.navButtons);
    }

    _listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.listeners.push(() => target.removeEventListener(type, handler, options));
    }

    _bind() {
      this.onMouseEnter = () => {
        this.hovered = true;
        this._syncAutoplay();
      };
      this.onMouseLeave = () => {
        this.hovered = false;
        this._syncAutoplay();
      };
      this.onPointerDown = () => {
        this.held = true;
        this._syncAutoplay();
      };
      this.onPointerUp = () => {
        this.held = false;
        this._syncAutoplay();
      };
      this.onFocusIn = (event) => {
        this.focused = true;
        const dot = event.target.closest && event.target.closest('[data-reviews-dot]');
        if (dot && this.dotHost.contains(dot)) this._updateDotWindow(Number(dot.dataset.reviewsDot));
        this._syncAutoplay();
      };
      this.onFocusOut = () => {
        window.clearTimeout(this.focusTimer);
        this.focusTimer = window.setTimeout(() => {
          this.focusTimer = null;
          this.focused = this._containsFocus();
          this._updateDotWindow(this.activeIndex);
          this._syncAutoplay();
        }, 0);
      };
      this.onVisibilityChange = () => this._syncAutoplay();
      this.onMotionPreferenceChange = () => this._syncAutoplay();
      this.onScroll = () => this._requestPositionSync();
      this.onResize = () => this._requestPositionSync();
      this.onTrackPointerDown = (event) => {
        /* Touch already gets the browser's native, momentum-based scrolling.
           Add the missing mouse/pen equivalent without taking that path over. */
        if (event.pointerType === 'touch' || event.button !== 0 || event.isPrimary === false) return;
        if (this.track.scrollWidth <= this.track.clientWidth + 1) return;
        if (event.target.closest && event.target.closest(INTERACTIVE_SELECTOR)) return;

        window.clearTimeout(this.clickTimer);
        this.clickTimer = null;
        this.suppressClick = false;
        this.dragPointerId = event.pointerId;
        this.dragStartX = event.clientX;
        this.dragCurrentX = event.clientX;
        this.dragStartScrollLeft = this.track.scrollLeft;
        this.dragStartIndex = this._nearestCardIndex();
        this.dragging = false;
        this.track.classList.add('is-pointer-down');

        if (this.track.setPointerCapture) this.track.setPointerCapture(event.pointerId);
        event.preventDefault();
      };
      this.onTrackPointerMove = (event) => {
        if (event.pointerId !== this.dragPointerId) return;

        this.dragCurrentX = event.clientX;
        const delta = event.clientX - this.dragStartX;
        if (!this.dragging && Math.abs(delta) < DRAG_THRESHOLD) return;

        this.dragging = true;
        this.track.classList.add('is-dragging');
        this.track.scrollLeft = this.dragStartScrollLeft - delta;
        event.preventDefault();
      };
      this.onTrackPointerEnd = (event) => {
        if (event.pointerId !== this.dragPointerId) return;

        const pointerId = this.dragPointerId;
        const dragged = this.dragging;
        const targetIndex = dragged ? this._dragTargetIndex() : this._nearestCardIndex();
        this.dragPointerId = null;
        this.dragging = false;
        this.track.classList.remove('is-pointer-down', 'is-dragging');

        if (this.track.hasPointerCapture && this.track.hasPointerCapture(pointerId)) {
          this.track.releasePointerCapture(pointerId);
        }

        if (dragged) {
          /* A drag can finish with a synthetic click on whichever card is now
             under the pointer. Swallow only that click, then restore normal
             descendants immediately. */
          this.suppressClick = true;
          this.clickTimer = window.setTimeout(() => {
            this.clickTimer = null;
            this.suppressClick = false;
          }, 0);
          this._setActive(targetIndex);
          this._scrollToCard(targetIndex);
        } else {
          /* Restoring mobile scroll snap after a click can expose a position
             left between cards by an interrupted autoplay or resize. Settle it
             without treating the click as a directional gesture. */
          this._scrollToCard(targetIndex);
        }
      };
      this.onTrackClick = (event) => {
        if (!this.suppressClick) return;
        this.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      };
      this.onDotClick = (event) => {
        const button = event.target.closest && event.target.closest('[data-reviews-dot]');
        if (!button || !this.dotHost.contains(button)) return;

        const index = Number(button.dataset.reviewsDot);
        if (!Number.isInteger(index) || !this.cards[index]) return;

        this._stopAutoplay();
        this._setActive(index);
        this._scrollToCard(index);
        this._syncAutoplay();
      };

      /* Same shape as onDotClick: stop autoplay first so the row does not
         drift out from under a deliberate click, then let _syncAutoplay decide
         whether it may start again — it will not while the pointer is still
         over the control. */
      this.onNavClick = (event) => {
        const button = event.target.closest && event.target.closest('[data-reviews-nav-direction]');
        if (!button || !this.navHost || !this.navHost.contains(button)) return;

        this._stopAutoplay();
        if (button.dataset.reviewsNavDirection === 'prev') this._retreat();
        else this._advance();
        this._syncAutoplay();
      };

      for (const target of [this.track, this.dotHost, this.navHost]) {
        if (!target) continue;
        this._listen(target, 'mouseenter', this.onMouseEnter);
        this._listen(target, 'mouseleave', this.onMouseLeave);
        this._listen(target, 'pointerdown', this.onPointerDown);
        this._listen(target, 'focusin', this.onFocusIn);
        this._listen(target, 'focusout', this.onFocusOut);
      }

      this._listen(this.dotHost, 'click', this.onDotClick);
      if (this.navHost) this._listen(this.navHost, 'click', this.onNavClick);
      this._listen(this.track, 'pointerdown', this.onTrackPointerDown);
      this._listen(this.track, 'pointermove', this.onTrackPointerMove);
      this._listen(this.track, 'pointerup', this.onTrackPointerEnd);
      this._listen(this.track, 'pointercancel', this.onTrackPointerEnd);
      this._listen(this.track, 'lostpointercapture', this.onTrackPointerEnd);
      this._listen(this.track, 'click', this.onTrackClick, true);
      this._listen(this.track, 'scroll', this.onScroll, { passive: true });
      this._listen(window, 'pointerup', this.onPointerUp);
      this._listen(window, 'pointercancel', this.onPointerUp);
      this._listen(window, 'resize', this.onResize, { passive: true });
      this._listen(document, 'visibilitychange', this.onVisibilityChange);

      if (this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener('change', this.onMotionPreferenceChange);
        this.listeners.push(() =>
          this.reducedMotion.removeEventListener('change', this.onMotionPreferenceChange)
        );
      } else if (this.reducedMotion.addListener) {
        this.reducedMotion.addListener(this.onMotionPreferenceChange);
        this.listeners.push(() => this.reducedMotion.removeListener(this.onMotionPreferenceChange));
      }
    }

    _observe() {
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.onScreen = Boolean(entries[0] && entries[0].isIntersecting);
          this._syncAutoplay();
        }, { threshold: 0.2 });
        this.visibilityObserver.observe(this.track);

        /* IntersectionObserver supplies low-cost position changes as cards
           enter the scroll port. The passive scroll handler below is retained
           as the deterministic fallback and settles ties against the snap
           origin instead of relying on callback entry order. */
        this.cardObserver = new IntersectionObserver(
          () => this._requestPositionSync(),
          { root: this.track, threshold: [0, 0.25, 0.5, 0.75, 1] }
        );
        this.cards.forEach((card) => this.cardObserver.observe(card));
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this._requestPositionSync());
        this.resizeObserver.observe(this.track);
      }
    }

    _containsFocus() {
      return this.track.contains(document.activeElement) ||
        this.dotHost.contains(document.activeElement) ||
        Boolean(this.navHost && this.navHost.contains(document.activeElement));
    }

    _requestPositionSync() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this._setActive(this._nearestCardIndex());
      });
    }

    _snapOrigin() {
      const styles = window.getComputedStyle(this.track);
      const padding = parseFloat(styles.scrollPaddingLeft) || 0;
      return this.track.getBoundingClientRect().left + padding;
    }

    _nearestCardIndex() {
      const origin = this._snapOrigin();
      let nearest = 0;
      let distance = Infinity;

      this.cards.forEach((card, index) => {
        const nextDistance = Math.abs(card.getBoundingClientRect().left - origin);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = index;
        }
      });

      return nearest;
    }

    _dragTargetIndex() {
      const nearest = this._nearestCardIndex();
      const startCard = this.cards[this.dragStartIndex];
      const cardWidth = startCard ? startCard.getBoundingClientRect().width : 0;
      const commitDistance = Math.min(
        cardWidth ? cardWidth * DRAG_COMMIT_RATIO : DRAG_COMMIT_MAX,
        DRAG_COMMIT_MAX
      );
      const distance = this.dragCurrentX - this.dragStartX;

      /* A mouse has no native flick momentum here: scrollLeft follows the
         pointer exactly. Give a deliberate, modest drag the same decisive feel
         as a swipe by committing at least one card in its direction. Longer
         drags still land on whichever farther card is already nearest. */
      if (Math.abs(distance) < commitDistance) return nearest;

      const direction = distance < 0 ? 1 : -1;
      const intended = Math.max(
        0,
        Math.min(this.dragStartIndex + direction, this.cards.length - 1)
      );

      return direction > 0 ? Math.max(nearest, intended) : Math.min(nearest, intended);
    }

    _setActive(index, force = false) {
      /* A desktop-to-mobile resize can give a previously display:none dot rail
         its first measurable offsets without changing the active card. Keep
         the window geometry fresh even when the semantic state is unchanged. */
      if (!force && index === this.activeIndex) {
        this._updateDotWindow(index);
        return;
      }
      this.activeIndex = index;

      this.dotButtons.forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      this._updateDotWindow(index);
    }

    _updateDotWindow(index) {
      if (!this.dotButtons || this.dotButtons.length <= MAX_VISIBLE_DOTS) {
        if (this.dotTrack) this.dotTrack.style.transform = '';
        return;
      }

      const maxStart = this.dotButtons.length - MAX_VISIBLE_DOTS;
      const start = Math.min(Math.max(index - Math.floor(MAX_VISIBLE_DOTS / 2), 0), maxStart);
      const first = this.dotButtons[0];
      const target = this.dotButtons[start];
      const offset = target.offsetLeft - first.offsetLeft;

      this.dotTrack.dataset.windowStart = String(start);
      /* overflow:hidden is still a programmatically scrollable box. Reset any
         scroll the browser performed while tabbing to a clipped button; the
         translated track is the sole owner of the visible window. */
      this.dotHost.scrollLeft = 0;
      /* When the rail is display:none at a desktop breakpoint, offsetLeft is
         zero. Resize/ResizeObserver calls this again as soon as mobile CSS
         gives it a box. */
      this.dotTrack.style.transform = offset ? `translateX(${-offset}px)` : '';
    }

    _scrollToCard(index) {
      const card = this.cards[index];
      const max = this.track.scrollWidth - this.track.clientWidth;
      const delta = card.getBoundingClientRect().left - this._snapOrigin();
      const target = Math.max(0, Math.min(this.track.scrollLeft + delta, max));

      this.track.scrollTo({
        left: target,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    _behavior() {
      return this.reducedMotion.matches ? 'auto' : 'smooth';
    }

    /* Ported from the former inline implementation: advance to the first card
       beyond the snap origin, and wrap once the scroll port reaches its end.

       These compute the card offset themselves rather than leaning on CSS
       scroll-snap, which is what lets the section drop snapping above the
       mobile rail without the arrows or autoplay losing their card alignment.

       Reduced motion is honoured here now that the next arrow calls it too.
       Autoplay never reaches this under that preference — _shouldAutoplay
       refuses — so the hardcoded smooth scroll was unobservable until there
       was a button. */
    _advance() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max < 1) return;

      if (max - this.track.scrollLeft <= 2) {
        this.track.scrollTo({ left: 0, behavior: this._behavior() });
        return;
      }

      const origin = this._snapOrigin();
      let target = max;

      for (const card of this.cards) {
        const delta = card.getBoundingClientRect().left - origin;
        if (delta > 1) {
          target = Math.min(this.track.scrollLeft + delta, max);
          break;
        }
      }

      this.track.scrollTo({ left: target, behavior: this._behavior() });
    }

    /* The mirror of _advance: the LAST card still left of the origin is the
       previous one, so this keeps overwriting rather than breaking on the
       first match. Wraps to the end from 0, because _advance wraps to 0 from
       the end and a pair of arrows that disagree about whether the row is a
       loop is worse than either answer on its own. */
    _retreat() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max < 1) return;

      if (this.track.scrollLeft <= 2) {
        this.track.scrollTo({ left: max, behavior: this._behavior() });
        return;
      }

      const origin = this._snapOrigin();
      let target = 0;

      for (const card of this.cards) {
        const delta = card.getBoundingClientRect().left - origin;
        if (delta < -1) target = Math.max(0, this.track.scrollLeft + delta);
      }

      this.track.scrollTo({ left: target, behavior: this._behavior() });
    }

    _shouldAutoplay() {
      return this.autoplayInterval > 0 &&
        this.cards.length > 1 &&
        this.onScreen &&
        !this.hovered &&
        !this.held &&
        !this.focused &&
        !this.reducedMotion.matches &&
        !document.hidden;
    }

    _stopAutoplay() {
      if (!this.timer) return;
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    _syncAutoplay() {
      if (!this._shouldAutoplay()) {
        this._stopAutoplay();
        return;
      }

      if (!this.timer) {
        this.timer = window.setTimeout(() => {
          this.timer = null;
          this._advance();
          this._syncAutoplay();
        }, this.autoplayInterval);
      }
    }

    /* Called by the Judge.me feed after it appends a batch. Every listener is
       delegated to the track or a control host, so only the card list, the
       ids, the dots and the card observer have to catch up. */
    refresh() {
      const known = new Set(this.cards);
      this.cards = [...this.track.querySelectorAll(CARD_SELECTOR)];
      this._ensureIds();
      this._buildDots();
      if (this.cardObserver) {
        this.cards.forEach((card) => {
          if (!known.has(card)) this.cardObserver.observe(card);
        });
      }
      this._setActive(this._nearestCardIndex(), true);
      this._syncAutoplay();
    }

    destroy() {
      this._stopAutoplay();
      window.clearTimeout(this.focusTimer);
      window.clearTimeout(this.clickTimer);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.cardObserver) this.cardObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.listeners.splice(0).forEach((remove) => remove());
      this.track.classList.remove('is-pointer-down', 'is-dragging');

      if (this.generatedDotHost && this.dotHost) this.dotHost.remove();
      else if (this.dotHost) this.dotHost.replaceChildren();
      /* Always server-rendered, so it is emptied rather than removed; leaving
         its buttons behind would give a rebuilt section two pairs of arrows,
         one of them wired to a destroyed instance. */
      if (this.navHost) this.navHost.replaceChildren();
      if (this.generatedTrackId && this.track) this.track.removeAttribute('id');
      this.generatedCardIds.forEach((card) => card.removeAttribute('id'));

      delete this.root.dataset.reviewsInitialized;
      delete this.root.__reviewsCarousel;
      instances.delete(this);
    }
  }

  const textOf = (element) => (element ? element.textContent.replace(/\s+/g, ' ').trim() : '');

  /* Loose on purpose: case, punctuation and spacing differ between a quote
     typed into a product and the same review in Judge.me. */
  const quoteKey = (text) => text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim().slice(0, 60);

  const fill = (element, text) => {
    if (!element) return;
    if (text) element.textContent = text;
    else element.remove();
  };

  /**
   * Judge.me reviews behind the hand-picked quotes.
   *
   * sections/reviews.liquid switches this on with data-judgeme-* attributes on
   * a product page that has Judge.me reviews, and supplies the card as a
   * <template>, so the card's structure is written once, in Liquid. This only
   * fetches, filters and fills it in.
   *
   * The endpoint is the one Judge.me's own review list pages through: public,
   * no token, open to cross-origin requests. It answers with that list's
   * markup, so each review is read out of an inert parsed document and only
   * ever written back as text. Nothing Judge.me sends is inserted as HTML.
   *
   * The first request goes out when the section is a screen away, the next
   * whenever the row's last card comes within two row-widths of view, until
   * FEED_MAX Judge.me cards are in. Any failure ends the feed quietly and
   * leaves the page as it is.
   */
  class JudgemeFeed {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.rev__track');
      this.cardTemplate = root.querySelector('template[data-reviews-card-template]');
      this.starTemplate = root.querySelector('template[data-reviews-star-template]');
      this.endpoint = root.dataset.judgemeUrl;
      this.perPage = numberOrZero(root.dataset.judgemePerPage) || FEED_PER_PAGE;
      this.total = numberOrZero(root.dataset.judgemeTotal);
      this.showTitles = !isFalse(root.dataset.reviewsTitles);
      this.query = {
        url: root.dataset.judgemeShop,
        shop_domain: root.dataset.judgemeShop,
        platform: 'shopify',
        product_id: root.dataset.judgemeProduct
      };
      this.page = 0;
      this.count = 0;
      this.loading = false;
      this.destroyed = false;
      this.controller = null;
      this.nearObserver = null;
      this.endObserver = null;
      this.seen = new Set();
      this.quotes = new Set(
        [...root.querySelectorAll(`${CARD_SELECTOR} .rev__quote`)].map((quote) => quoteKey(textOf(quote)))
      );
      this.done = !this.track || !this.cardTemplate || !this.endpoint ||
        !this.query.shop_domain || !this.query.product_id ||
        !('IntersectionObserver' in window) || typeof window.fetch !== 'function';

      root.__reviewsFeed = this;
      feeds.add(this);
      if (this.done) return;

      /* Watched on the section's wrapper: while the section waits for its first
         card it is display:none and has no box of its own to intersect. */
      this.nearObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.nearObserver.disconnect();
        this._load();
      }, { rootMargin: '100% 0px' });
      this.nearObserver.observe(root.closest('.shopify-section') || root);

      this.endObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this._load();
      }, { root: this.track, rootMargin: '0px 200% 0px 0px' });
    }

    async _load() {
      if (this.loading || this.done) return;
      this.loading = true;
      this.controller = new AbortController();
      const timeout = window.setTimeout(() => this.controller.abort(), FEED_TIMEOUT);
      const page = this.page + 1;

      try {
        const params = new URLSearchParams({ ...this.query, page: String(page), per_page: String(this.perPage) });
        const response = await fetch(`${this.endpoint}?${params}`, {
          signal: this.controller.signal,
          credentials: 'omit'
        });
        if (!response.ok) throw new Error(`Judge.me answered ${response.status}`);
        const data = await response.json();
        const reviews = this._parse(String(data.html || ''));
        const total = numberOrZero(data.total_count) || this.total;
        this.page = page;
        if (!reviews.length || page * this.perPage >= total) this.done = true;
        this._append(reviews);
      } catch (error) {
        this.done = true;
      } finally {
        window.clearTimeout(timeout);
        this.controller = null;
        this.loading = false;
      }

      this._watchEnd();
    }

    _parse(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return [...doc.querySelectorAll('.jdgm-rev')].map((review) => {
        const score = review.querySelector('[data-score]');
        return {
          id: review.getAttribute('data-review-id') || '',
          rating: score ? Number(score.getAttribute('data-score')) || 0 : 0,
          title: textOf(review.querySelector('.jdgm-rev__title')),
          body: textOf(review.querySelector('.jdgm-rev__body')),
          author: textOf(review.querySelector('.jdgm-rev__author'))
        };
      });
    }

    _append(reviews) {
      if (this.destroyed) return;
      const fragment = document.createDocumentFragment();

      for (const review of reviews) {
        if (this.count >= FEED_MAX) break;
        if (review.id) {
          if (this.seen.has(review.id)) continue;
          this.seen.add(review.id);
        }
        if (review.body.length < FEED_MIN_TEXT) continue;
        const key = quoteKey(review.body);
        if (this.quotes.has(key)) continue;
        this.quotes.add(key);
        fragment.appendChild(this._card(review));
        this.count++;
      }
      if (this.count >= FEED_MAX) this.done = true;

      if (!fragment.childNodes.length) return;
      this.track.appendChild(fragment);
      this.root.removeAttribute('data-reviews-pending');
      if (this.root.__reviewsCarousel) this.root.__reviewsCarousel.refresh();
      else init(this.root);
    }

    _card(review) {
      const card = this.cardTemplate.content.firstElementChild.cloneNode(true);
      const stars = card.querySelector('.rev__stars');
      const rating = Math.max(0, Math.min(5, Math.round(review.rating)));

      if (stars && rating && this.starTemplate) {
        stars.setAttribute('aria-label', `${rating} out of 5 stars`);
        for (let i = 0; i < rating; i++) {
          stars.appendChild(this.starTemplate.content.firstElementChild.cloneNode(true));
        }
      } else if (stars) {
        stars.remove();
      }

      /* The same fold the Liquid applies when headlines are switched off. */
      let { title, body } = review;
      if (!this.showTitles && title) {
        body = `${title} ${body}`;
        title = '';
      }
      fill(card.querySelector('.rev__headline'), title);
      fill(card.querySelector('.rev__quote'), body);
      fill(card.querySelector('.rev__author'), review.author);

      /* reveal.js staged the cards that were here when it ran. One arriving
         later would sit in the hidden start state for good. */
      if (this.root.classList.contains('reveal')) {
        card.classList.add('reveal--item-in', 'reveal--item-done');
      }
      return card;
    }

    /* Re-aimed at the newest last card after every batch. Observing reports
       straight away, so a batch that left the end still in reach — or that the
       filter emptied — asks for the next without waiting for a scroll. A
       section still waiting for its first card has no box to watch, so it
       simply asks again. */
    _watchEnd() {
      if (this.done || this.destroyed) return;
      if (this.root.hasAttribute('data-reviews-pending')) {
        this._load();
        return;
      }
      const cards = this.track.querySelectorAll(CARD_SELECTOR);
      const last = cards[cards.length - 1];
      if (!last) return;
      this.endObserver.disconnect();
      this.endObserver.observe(last);
    }

    destroy() {
      this.destroyed = true;
      this.done = true;
      if (this.controller) this.controller.abort();
      if (this.nearObserver) this.nearObserver.disconnect();
      if (this.endObserver) this.endObserver.disconnect();
      delete this.root.__reviewsFeed;
      feeds.delete(this);
    }
  }

  const rootsWithin = (scope) => {
    const roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    return roots;
  };

  const init = (scope = document) => {
    /* Clean up a section replaced without an unload event before claiming the
       fresh copy. This is cheap: a page normally has one reviews section. */
    for (const instance of [...instances]) {
      if (!instance.root.isConnected) instance.destroy();
    }
    for (const feed of [...feeds]) {
      if (!feed.root.isConnected) feed.destroy();
    }

    for (const root of rootsWithin(scope)) {
      /* Before the carousel: a section waiting on Judge.me has no cards yet,
         so the carousel declines it and the feed calls back in once it has. */
      if (root.dataset.judgemeProduct && !root.__reviewsFeed) new JudgemeFeed(root);
      if (root.__reviewsCarousel) continue;
      const instance = new ReviewsCarousel(root);
      if (instance.track && instance.cards.length) instances.add(instance);
    }
  };

  const destroyWithin = (scope) => {
    for (const instance of [...instances]) {
      if (scope === instance.root || (scope.contains && scope.contains(instance.root))) {
        instance.destroy();
      }
    }
    for (const feed of [...feeds]) {
      if (scope === feed.root || (scope.contains && scope.contains(feed.root))) {
        feed.destroy();
      }
    }
  };

  window.__liturgyReviews = { init, destroyWithin };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
